import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Crown, User, Plus, Share2, MessageCircle, Copy, Check, Zap, Trophy, Settings, Sparkles } from 'lucide-react';
import { getMisl, subscribeMisl, type StoredMisl } from './mislStore';
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-2161" data-mp="1tuekbxb3o3kx" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-2162" data-mp="w9saad141u6f" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-2163" data-mp="114xr4xfnoinn" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-2164" data-mp="1awva4qcwoewx" />
  </>;
export const FoundingMislStatus = ({
  onManage
}: {
  onManage: () => void;
}) => {
  const [misl, setMislState] = useState<StoredMisl | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setMislState(getMisl());
    const unsub = subscribeMisl(() => setMislState(getMisl()));
    return unsub;
  }, []);
  if (!misl) return null;
  const memberCount = misl.members.length;
  const progress = Math.min(100, Math.round(memberCount / misl.target * 100));
  const isActive = memberCount >= misl.target;
  const inviteLink = `lahoretreasury.com/misl/${misl.name.toLowerCase().replace(/\s+/g, '-')}`;
  const remaining = Math.max(0, misl.target - memberCount);
  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(`https://${inviteLink}`).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`🛡️ Join my Misl on Lahore Treasury — ${misl.name}.\nWe need ${remaining} more to activate our house in the Court.\n\n${inviteLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };
  const nextSteps = isActive ? [{
    icon: Trophy,
    label: 'Climb the Misl leaderboard with your members'
  }, {
    icon: Sparkles,
    label: 'Unlock group rewards and Court standing together'
  }] : [{
    icon: Share2,
    label: `Invite ${remaining} more ${remaining === 1 ? 'member' : 'members'} to activate your house`
  }, {
    icon: Zap,
    label: 'Every member you bring in can also invite'
  }, {
    icon: Trophy,
    label: 'Reach 5 members to unlock the Misl leaderboard'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="relative border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A1410] via-[#131B2B] to-[#0B0F19] overflow-hidden" data-id="element-2165" data-mp="157k1bjktdyr4">
      <CornerOrnaments data-id="element-2166" data-mp="3idh1bm4jspxm" />
      <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2167" data-mp="3icyyg6ndrdgq" />
      <div className="absolute -right-20 -top-20 w-56 h-56 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-2168" data-mp="3dh9r3h54cs9v" />
      <div className="absolute -left-20 -bottom-20 w-56 h-56 bg-[#8B0000]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-2169" data-mp="3ndmkedwr5443" />

      {/* Header strip */}
      <div className="relative z-10 px-5 sm:px-7 py-5 border-b border-[#D4AF37]/20 flex items-start sm:items-center justify-between gap-4 flex-wrap" data-id="element-2170" data-mp="2zoabzg99102u">
        <div className="flex items-center gap-4" data-id="element-2171" data-mp="39dbv3fb3bxl1">
          <motion.div animate={{
          rotate: [0, -3, 3, -3, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          repeatDelay: 3
        }} className="shrink-0 w-12 h-12 border-2 border-[#D4AF37]/60 bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-2172" data-mp="39f53p059krvo">
            <Crown className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} data-id="element-2173" data-mp="1f7m2qnj6fn30" />
          </motion.div>
          <div className="min-w-0" data-id="element-2174" data-mp="1j7ms337tdiou">
            <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase font-bold mb-1 flex items-center gap-2" data-id="element-2175" data-mp="1j8o357oty56k">
              <Sparkles className="w-3 h-3" data-id="element-2176" data-mp="1o5oadlxf1dz9" />
              Your Founding Misl
            </p>
            <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] leading-tight truncate" data-id="element-2177" data-mp="1t0anbtj5belg">
              {misl.name}
            </h3>
            {misl.region && <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase mt-0.5" data-id="element-2178" data-mp="22uu5oyt4uk1v">
                {misl.region}
              </p>}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0" data-id="element-2179" data-mp="2bw0ndb7iq0wp">
          <div className={`flex items-center gap-2 px-3 py-2 border text-[9px] tracking-[0.3em] uppercase font-bold ${isActive ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#FDFBF7]/30 bg-[#FDFBF7]/5 text-[#FDFBF7]/80'}`} data-id="element-2180" data-mp="cjzffs8640bf">
            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#D4AF37]' : 'bg-[#FDFBF7]/60 animate-pulse'} shadow-[0_0_8px_currentColor]`} data-id="element-2181" data-mp="a0um5nwnquc2" />
            {isActive ? 'Active' : 'Forming'}
          </div>
          <button onClick={onManage} className="hidden sm:inline-flex items-center gap-2 px-3.5 py-2 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/60 text-[10px] tracking-[0.25em] uppercase font-bold transition-colors" data-id="element-2182" data-mp="3rgb60u3a3lot">
            <Settings className="w-3 h-3" data-id="element-2183" data-mp="izy3ong6f1og" />
            Manage
          </button>
        </div>
      </div>

      {/* Progress section */}
      <div className="relative z-10 px-5 sm:px-7 py-5 border-b border-[#D4AF37]/20" data-id="element-2184" data-mp="exur1vhrtr1l">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2" data-id="element-2185" data-mp="33qp4hzzfoudn">
          <div data-id="element-2186" data-mp="2zp3kluohffty">
            <p className="text-[#FDFBF7]/45 text-[10px] tracking-[0.25em] uppercase mb-1" data-id="element-2187" data-mp="2zolr35t7ffpq">
              Members
            </p>
            <p className="font-serif text-4xl text-[#FDFBF7] leading-none" data-id="element-2188" data-mp="316k3iv612pdv">
              {memberCount}{' '}
              <span className="text-[#FDFBF7]/30" data-id="element-2189" data-mp="364c5ki0dzrvs">/ {misl.target}</span>
            </p>
          </div>
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium" data-id="element-2190" data-mp="2r7l29xnldw0w">
            {isActive ? '🔥 House Activated' : `${remaining} more to activate`}
          </p>
        </div>
        <div className="h-2 bg-[#D4AF37]/10 overflow-hidden" data-id="element-2191" data-mp="2ie85ulqbkca2">
          <motion.div animate={{
          width: `${progress}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="h-full bg-gradient-to-r from-[#D4AF37]/60 via-[#D4AF37] to-[#E5C158] shadow-[0_0_15px_rgba(212,175,55,0.4)]" data-id="element-2192" data-mp="2n6httw7vj29p" />
        </div>
      </div>

      {/* Member list + What's next */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-[#D4AF37]/15" data-id="element-2193" data-mp="23h7beagqu37w">
        {/* Members */}
        <div className="px-5 sm:px-7 py-5" data-id="element-2194" data-mp="1kw2i9lvwnjf4">
          <div className="flex items-center justify-between mb-3" data-id="element-2195" data-mp="1fvern1bl94lf">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-2196" data-mp="1fx82ytx5cczr">
              Members
            </p>
            <span className="font-mono text-[#D4AF37] text-xs" data-id="element-2197" data-mp="1ov1wjx3j0sj8">
              {memberCount} / {misl.target}
            </span>
          </div>
          <div className="space-y-2" data-id="element-2198" data-mp="23r9eeqrgphvc">
            {misl.members.map(m => <motion.div key={m.id} initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex items-center gap-3 px-3 py-2 border border-[#D4AF37]/10 bg-[#0B0F19]/40 hover:border-[#D4AF37]/30 transition-colors" data-id="element-2199" data-mp="1ysofqvruz690">
                <div className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center ${m.role === 'founder' ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 bg-[#131B2B]'}`} data-id="element-2200" data-mp="2hlscwr4ac18d">
                  {m.role === 'founder' ? <Crown className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2201" data-mp="1t9zj0200l2l" /> : <User className="w-3.5 h-3.5 text-[#FDFBF7]/50" data-id="element-2202" data-mp="6q0etl5zbcmy" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-2203" data-mp="35a06pi9bh45v">
                  <p className="font-serif text-sm text-[#FDFBF7] leading-tight truncate" data-id="element-2204" data-mp="3a7s1ql59d6ea">
                    {m.name}
                  </p>
                  <p className="font-mono text-[#FDFBF7]/40 text-[10px] truncate" data-id="element-2205" data-mp="3f639mqbzt6tv">
                    {m.handle} · {m.joinedAt}
                  </p>
                </div>
                {m.role === 'founder' && <span className="shrink-0 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-0.5" data-id="element-2206" data-mp="2mho5wzybaaxu">
                    Founder
                  </span>}
                <button onClick={handleWhatsApp} className="shrink-0 text-[#D4AF37]/60 hover:text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 px-2 py-1 transition-colors flex items-center gap-1" data-id="element-2207" data-mp="2jvf45m2azn20">
                  <Share2 className="w-2.5 h-2.5" data-id="element-2208" data-mp="20yx48hdawguo" />
                  Invite
                </button>
              </motion.div>)}
            {Array.from({
            length: remaining
          }).map((_, i) => <div key={`empty-${i}`} className="flex items-center gap-3 px-3 py-2 border border-dashed border-[#D4AF37]/15 bg-[#0B0F19]/20" data-id="element-2209" data-mp="1r8ayrfzat6pp" data-mpi={i}>
                <div className="w-8 h-8 shrink-0 rounded-full border border-dashed border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-2210" data-mp="1hbg1t1iobedj">
                  <Plus className="w-3 h-3 text-[#D4AF37]/30" data-id="element-2211" data-mp="1dbfltcfxev4d" />
                </div>
                <p className="text-[#FDFBF7]/30 text-xs italic flex-1" data-id="element-2212" data-mp="2ko703id0yaac">
                  Empty seat
                </p>
                <button onClick={onManage} className="shrink-0 text-[#FDFBF7]/35 hover:text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase transition-colors" data-id="element-2213" data-mp="2fokjr01wgdxb">
                  Invite
                </button>
              </div>)}
          </div>
        </div>

        {/* What's next */}
        <div className="px-5 sm:px-7 py-5" data-id="element-2214" data-mp="1wsuhdbq21fiw">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-2215" data-mp="1wu5mbev8ktbl">
            What&rsquo;s Next
          </p>
          <ul className="space-y-3 mb-5" data-id="element-2216" data-mp="26ph3s4y14en1">
            {nextSteps.map((s, i) => <li key={i} className="flex items-start gap-3" data-id="element-2217" data-mp="3m6r1ab0chfuv" data-mpi={i}>
                <div className="w-7 h-7 shrink-0 border border-[#D4AF37]/30 bg-[#131B2B] flex items-center justify-center" data-id="element-2218" data-mp="3m4xqcjip89h2">
                  <s.icon className="w-3 h-3 text-[#D4AF37]" data-id="element-2219" data-mp="3i7i8b8d4pm0d" />
                </div>
                <p className="text-[#FDFBF7]/70 text-xs leading-relaxed pt-1" data-id="element-2220" data-mp="3pqreksqc94l7">
                  {s.label}
                </p>
              </li>)}
          </ul>

          {/* Invite block */}
          {!isActive && <div className="border border-[#D4AF37]/25 bg-[#0B0F19]/60 p-3" data-id="element-2221" data-mp="3kpbr6u0oulzn">
              <div className="flex items-center gap-2 border border-[#D4AF37]/20 bg-[#0B0F19] px-2.5 py-2 mb-2.5" data-id="element-2222" data-mp="3kreqy5rn7thk">
                <span className="font-mono text-[#D4AF37]/80 text-[10px] truncate flex-1" data-id="element-2223" data-mp="h8lk0btde2wd">
                  {inviteLink}
                </span>
                <button onClick={handleCopy} className="shrink-0 flex items-center gap-1 px-2 py-0.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2224" data-mp="2j74sn344r92">
                  {copied ? <>
                      <Check className="w-2.5 h-2.5" data-id="element-2225" data-mp="1017byl15p9bo" />
                      Copied
                    </> : <>
                      <Copy className="w-2.5 h-2.5" data-id="element-2226" data-mp="14xjq4sfrrpav" />
                      Copy
                    </>}
                </button>
              </div>
              <button onClick={handleWhatsApp} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-2227" data-mp="vvtx5i68oiaz">
                <MessageCircle className="w-3.5 h-3.5" data-id="element-2228" data-mp="m3xml7myuzpv" />
                Share on WhatsApp
              </button>
            </div>}

          <button onClick={onManage} className="sm:hidden w-full mt-3 inline-flex items-center justify-center gap-2 px-3.5 py-2.5 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/60 text-[10px] tracking-[0.25em] uppercase font-bold transition-colors" data-id="element-2229" data-mp="d5vi2i40xoxu">
            <Settings className="w-3 h-3" data-id="element-2230" data-mp="2etq6r11mf53a" />
            Manage Misl
          </button>
        </div>
      </div>
    </motion.div>;
};